import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { useRef, useState, useCallback, useEffect } from 'react';
import { EGDSLink } from '../../link/link/index.esm.js';
import '../../link/types/index.esm.js';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { EGDSExpandoTriggerStates } from '../types/index.esm.js';
import styles from '../../styles/uitk-egds-expando.css';
import { classNames } from '../../utils/classnames/index.esm.js';
import { useEGDSContext, EGDSContextProvider } from '../../utils/context-provider/index.esm.js';
const getClassNameList = classNames.bind(styles);
const ExpandoPeek = props => {
  const {
    isVisible = false,
    expandLabel,
    expandA11yLabel,
    collapseLabel,
    collapseA11yLabel,
    l10n,
    handleClick,
    hideScrim,
    analytics,
    className,
    children,
    lines,
    items,
    peekControlRef,
    revealRef,
    ...attrs
  } = props;
  const forwardContext = useEGDSContext();
  const {
    formatText
  } = useLocalization();
  const innerDomRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [expandoHeightRef, setExpandoHeightRef] = useState({
    scrollHeight: 0,
    clientHeight: 0
  });
  const expandoPeekInnerClassName = getClassNameList('uitk-expando-peek-inner', {
    'display-lines': lines,
    'display-items': items
  });
  const updateDimensions = useCallback(() => {
    if (innerDomRef?.current) {
      setExpandoHeightRef({
        scrollHeight: innerDomRef.current.scrollHeight,
        clientHeight: innerDomRef.current.clientHeight
      });
    }
  }, []);
  // Initial calculation and setup ResizeObserver
  useEffect(() => {
    // Initial measurement
    updateDimensions();
    // Setup ResizeObserver
    if (typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined' && innerDomRef.current && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserverRef.current.observe(innerDomRef.current);
    }
    // Cleanup on unmount
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [updateDimensions]);
  // Update dimensions when children change
  useEffect(() => {
    updateDimensions();
  }, [children, updateDimensions]);
  useEffect(() => {
    if (isVisible) {
      revealRef?.current.focus();
    }
  }, [isVisible, revealRef]);
  const renderExpandoButton = () => {
    const btnText = isVisible ? collapseLabel || l10n.formatText('expando.collapseLabel') : expandLabel || l10n.formatText('expando.expandLabel');
    const btnA11yText = isVisible ? collapseA11yLabel || l10n.formatText('expando.collapseLabel') : expandA11yLabel || l10n.formatText('expando.expandLabel');
    const localAnalyticsId = isVisible ? EGDSExpandoTriggerStates.get('collapse') : EGDSExpandoTriggerStates.get('expand');
    const btnClass = getClassNameList('uitk-expando-peek-link');
    const controlClasses = getClassNameList('uitk-expando-peek-control', {
      'uitk-expando-peek-control-active': isVisible
    });
    return jsx("div", {
      className: controlClasses,
      children: jsx(EGDSLink, {
        "aria-expanded": isVisible,
        className: btnClass,
        analytics: analytics && {
          localAnalyticsId,
          ...analytics
        },
        domRef: peekControlRef,
        children: jsx("button", {
          type: "button",
          onClick: handleClick,
          "aria-label": btnA11yText,
          children: btnText
        })
      })
    });
  };
  const wrapperClasses = getClassNameList('uitk-expando-peek', className);
  let childCount = React.Children.count(children);
  if (childCount === 1) {
    childCount = React.Children.count(children.props.children);
  }
  const expandoCustomProperties = {
    '--uitk-expando-peek-lines-to-clamp': lines
  };
  const showExpando = React.useMemo(() => {
    // For item-based expansion, we always show the button if there are more items than the limit
    if (items && items < childCount) {
      return true;
    }
    // For line-based expansion, we need to check if content would be clipped when collapsed
    // We ignore the current height when expanded because we want to know if collapsing would clip content
    if (lines) {
      // If already expanded (isVisible), we show the button
      if (isVisible) {
        return true;
      }
      // If collapsed, we show the button only if content is being clipped
      const isScrollable = expandoHeightRef.scrollHeight - expandoHeightRef.clientHeight;
      const adjustedScrollHeight = expandoHeightRef.scrollHeight - (isScrollable === 1 ? 1 : 0);
      return expandoHeightRef.clientHeight < adjustedScrollHeight;
    }
    return false;
  }, [expandoHeightRef, lines, items, childCount, isVisible]);
  const mainClass = 'uitk-expando-peek-main';
  const mainClasses = getClassNameList(mainClass, {
    [`${mainClass}-active`]: isVisible,
    [`${mainClass}-inactive`]: !isVisible && showExpando,
    'hide-scrim': hideScrim
  });
  const contextData = {
    collapsed: !isVisible,
    srMessage: formatText('radio.button.hidden.items', items, childCount),
    ...forwardContext.data
  };
  return jsx(Fragment, {
    children: jsxs("div", {
      className: wrapperClasses,
      ...attrs,
      children: [jsx("div", {
        className: mainClasses,
        children: jsx("div", {
          className: expandoPeekInnerClassName,
          ref: innerDomRef,
          style: expandoCustomProperties,
          "data-items-to-display": isVisible ? undefined : items,
          children: jsx(EGDSContextProvider, {
            config: {
              context: 'uitk-expando-peek-context',
              data: contextData
            },
            children: children
          })
        })
      }), showExpando && renderExpandoButton()]
    })
  });
};
const EGDSExpandoPeek = props => {
  const l10n = useLocalization();
  return jsx(ExpandoPeek, {
    l10n: l10n,
    ...props
  });
};
export { EGDSExpandoPeek, ExpandoPeek };